import { useLingui } from "@lingui/react/macro";
import styles from "./TemplateActivityCategoryPicker.module.scss";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import {
  getActivityCategoryConfig,
  orderedCategories,
  type IActivityCategory,
} from "@medoma/models/activities";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { activityCategoryDictionary } from "@/api/Activities";
import { useFormContext, type FieldValues } from "react-hook-form";
import {
  useFieldPathWithPrefix,
  useFieldErrorWithPrefix,
} from "@/forms/FormFieldPathPrefixContext";
import type { TemplateActivityCategoryPickerFields } from "../templateFormSchema";

export const TemplateActivityCategoryPicker = ({
  onCategoryChange,
  templateIsWithoutPatient,
}: {
  onCategoryChange?: () => void;
  templateIsWithoutPatient: boolean;
}) => {
  const {
    formState: { errors },
    register,
  } = useFormContext<FieldValues>();

  const { t } = useLingui();
  const fieldName =
    useFieldPathWithPrefix<TemplateActivityCategoryPickerFields>("category");
  const errorMessage =
    useFieldErrorWithPrefix<TemplateActivityCategoryPickerFields>(
      errors,
      "category",
    );

  const filteredOrderedCategories = orderedCategories.filter((category) => {
    if (templateIsWithoutPatient) {
      const { requiresPatient } = getActivityCategoryConfig({
        category,
        activityHasPatient: false, // Value does not matter in this case
      });
      return !requiresPatient;
    }
    return true;
  });

  const CategoryRadioButton = (category: IActivityCategory) => (
    <div className={styles.categoryRadioButton}>
      <CategoryIcon category={category} size="small" />
      {t(activityCategoryDictionary[category])}
    </div>
  );

  return (
    <RadioButtons
      errorMessage={errorMessage}
      orientation="horizontal"
      legend={t`Typ av aktivitet`}
    >
      {filteredOrderedCategories.map((category) => (
        <RadioButton
          key={category}
          label={{
            text: t(activityCategoryDictionary[category]),
            component: CategoryRadioButton(category),
          }}
          visualStyle="framed"
          value={category}
          {...register(fieldName, {
            required: {
              value: true,
              message: t`Välj en kategori för aktiviteten`,
            },
            onChange: () => onCategoryChange?.(),
          })}
        />
      ))}
    </RadioButtons>
  );
};
